import gsap from 'gsap';
import { services } from '../data/services.js';

const BASE_ROTATION = {
    x: 0,
    y: (170 * Math.PI) / 180,
    z: (5 * Math.PI) / 180,
};

export default () => ({
    activeSrc: '',
    activeIndex: 0,
    get activeDescription() {
        return services[this.activeIndex]?.description ?? '';
    },
    modelMesh: null,
    container: null,
    model: null,

    _phoneModel: null,
    _videoElement: null,
    _isSpinning: false,

    loadVideoTexture(src, index) {
        // Selection state is updated before the model guard so the buttons still
        // respond while the three.js chunk is in flight.
        if (index) this.activeIndex = index - 1;
        if (!this._phoneModel) return;

        const swap = () => {
            if (this._videoElement) {
                this._videoElement.pause();
                this._videoElement.src = '';
            }
            this._videoElement = this._phoneModel.updateVideoTexture(src);
        };

        if (index) {
            this.spinModel(index);
            gsap.delayedCall(0.45, swap);
        } else {
            swap();
        }
    },

    spinModel(index) {
        if (!this.model) return;

        const direction = index % 2 === 1 ? -1 : 1;

        this._isSpinning = true;
        gsap.to(this.model.rotation, {
            x: BASE_ROTATION.x,
            y: BASE_ROTATION.y + Math.PI * 2 * direction,
            duration: 1,
            ease: 'power2.inOut',
            onComplete: () => {
                this.model.rotation.y = BASE_ROTATION.y;
                this._isSpinning = false;
            },
        });
    },

    updateModelImage(src, index) {
        if (!src) return;

        if (index) this.activeIndex = index - 1;
        this.activeSrc = src;

        if (!this._phoneModel) return;

        const swap = () => {
            if (this._videoElement) {
                this._videoElement.pause();
                this._videoElement.src = '';
                this._videoElement = null;
            }
            this._phoneModel.updateTexture(src);
        };

        if (index) {
            this.spinModel(index);
            gsap.delayedCall(0.45, swap);
        } else {
            swap();
        }
    },

    init() {
        this.activeSrc = services[0].src ?? '';
        this.container = document.getElementById('scene-container');
    },

    async loadModel() {
        if (!this.container || this._phoneModel) return;

        const { createPhoneModel } = await import('./phone-model.js');
        if (!this.container || this._phoneModel) return;

        const firstServiceIsVideo = services[0]['video'];

        try {
            this._phoneModel = createPhoneModel(this.container, {
                fill: 0.8,
                dropFraction: 0.1,
                rotation: BASE_ROTATION,
                position: { x: 0, y: 1, z: 0 },
                cameraPosition: { x: 0, y: 1, z: 3 },
                cameraFov: 45,
            });
        } catch {
            return;
        }

        this._phoneModel.ready.then(() => {
            this.model = this._phoneModel.getModel();
            this.modelMesh = this._phoneModel.getModelMesh();

            if (firstServiceIsVideo) {
                this.loadVideoTexture(this.activeSrc, null);
            } else {
                this.updateModelImage(this.activeSrc, null);
            }

            this._phoneModel.enableCursorFollow({
                target: this.container,
                baseRotation: BASE_ROTATION,
                intensity: 0.4,
                shouldSkip: () => this._isSpinning,
            });
        });
    },

    destroy() {
        if (this._videoElement) {
            this._videoElement.pause();
            this._videoElement.src = '';
            this._videoElement = null;
        }

        if (this._phoneModel) {
            this._phoneModel.destroy();
            this._phoneModel = null;
        }

        this.model = null;
        this.modelMesh = null;
        this.container = null;
    }
});
